import React, { useRef, useEffect } from 'react';
import { NetworkStatus } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Spinner from '@atlaskit/spinner/spinner';
import { xcss, Box } from '@atlaskit/primitives';

import { useFeatureCodesInURL } from '@confluence/feature-codes';
import { ErrorDisplay } from '@confluence/error-boundary';
import {
	CREATE_PAGE_EXPERIENCE,
	ExperienceTrackerContext,
	VIEW_CONTENT_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	VIEW_PAGE_EDIT_BUTTON_EXPERIENCE,
	VIEW_PAGE_SESSION_EXPERIENCE,
	LIVE_PAGE_VIEW_EXPERIENCE,
	useExperienceState,
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker';
import {
	FullPageEditorLoader,
	TreeRootForServerPlaceholderWrapper,
} from '@confluence/full-page-editor';
import type { NativeCollabInjectedProps } from '@confluence/native-collab';
import { NativeCollabLoader } from '@confluence/native-collab';
import { ContentViewedEvent } from '@confluence/content-body';
import { useLivePageStoreActions } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useLivePageOnboardingNudgeEligible } from '@confluence/onboarding-editor/entry-points/hooks/useLivePageOnboardingNudgeEligible';
import { getLivePageReleasePhase } from '@confluence/live-pages-utils/entry-points/isLivePagesFeatureEnabled';
import { fg } from '@confluence/feature-gating';

import { useEditorDataLoader } from './useEditorDataLoader';
import { triggerViewedAnalytics } from './LivePageViewedAnalytics';
import { usePermissionCheck } from './usePermissionCheck';
import { LivePagesChangeboardingManager } from './LivePagesChangeboardingManager';
import { LivePageSessionExperienceStart } from './LivePageSessionExperienceStart';
import { LivePageShortcuts } from './LivePageShortcuts';
import { StartLivePageLoad } from './perf.config';

export type LivePageComponentProps = {
	contentId: string;
	spaceKey: string | undefined;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	customPerfData?: Record<string, string | boolean>;
};
type LivePageContentProps = {
	canEdit: boolean;
	refetch: () => void;
	onNativeCollabLoad?: (contentId: string, viewMode: boolean | undefined) => void;
} & LivePageComponentProps;

const spinnerContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	height: '100vh',
});

// Handle aborting experiences
/* Why we're aborting each experience:
 * VIEW_PAGE_EXPERIENCE: Aborted due to not wanting live pages to affect core experience
 * VIEW_PAGE_SESSION_EXPERIENCE: Aborted due to not wanting live pages to affect classic view-page-specific experience. Live pages have their own live-page-session experience.
 * CREATE_PAGE_EXPERIENCE: Aborted due to not wanting live pages to affect core experience
 * VIEW_PAGE_EDIT_BUTTON_EXPERIENCE: Aborted due to editable live page starting but never finishing view-page/edit, because in ViewPageCommon, stickyHeader briefly mounts while loading `globalLiveState.isLive` and `editPermission`, which we unmount after we determine doc is an editable live page
 * VIEW_CONTENT_EXPERIENCE: Aborted due to live pages not integrating PerformanceMetrics.tsx, which is used to resolve this experience in normal pages.
 * Core experiences are not aborted if `live_page_exp_to_core_exp_migration` feature gate is enabled.
 */
const useAbortNonLiveExperiences = (isLivePageExpToCoreExpMigrationEnabled: boolean) => {
	const experienceTracker = React.useContext(ExperienceTrackerContext);
	const { hasStopped: hasViewPageExperienceStopped } =
		useExperienceState(VIEW_PAGE_EXPERIENCE) || {};
	const { hasStopped: hasViewPageSessionExperienceStopped } =
		useExperienceState(VIEW_PAGE_SESSION_EXPERIENCE) || {};
	const { hasStopped: hasCreatePageExperienceStopped } =
		useExperienceState(CREATE_PAGE_EXPERIENCE) || {};
	const { hasStopped: hasViewPageEditButtonExperienceStopped } =
		useExperienceState(VIEW_PAGE_EDIT_BUTTON_EXPERIENCE) || {};
	const { hasStopped: hasViewContentExperienceStopped } =
		useExperienceState(VIEW_CONTENT_EXPERIENCE) || {};

	const coreExperiencesToAbort = [
		{ name: VIEW_PAGE_EXPERIENCE, hasStopped: hasViewPageExperienceStopped },
		{
			name: VIEW_CONTENT_EXPERIENCE,
			hasStopped: hasViewContentExperienceStopped,
		},
		{
			name: CREATE_PAGE_EXPERIENCE,
			hasStopped: hasCreatePageExperienceStopped,
		},
	];
	const nonCoreExperiencesToAbort = [
		{ name: VIEW_PAGE_SESSION_EXPERIENCE, hasStopped: hasViewPageSessionExperienceStopped },
		{
			name: VIEW_PAGE_EDIT_BUTTON_EXPERIENCE,
			hasStopped: hasViewPageEditButtonExperienceStopped,
		},
	];

	const experiencesToAbort = isLivePageExpToCoreExpMigrationEnabled
		? nonCoreExperiencesToAbort
		: coreExperiencesToAbort.concat(nonCoreExperiencesToAbort);
	experiencesToAbort.forEach((abortedExperience) => {
		if (!abortedExperience.hasStopped) {
			experienceTracker.abort({
				name: abortedExperience.name,
				reason: 'Live Page Editor was opened',
			});
		}
	});
};

export const LivePageContent = ({
	contentId,
	spaceKey,
	canEdit,
	refetch,
	ClassicEditorContextProviderLoader,
	onNativeCollabLoad,
	customPerfData,
}: LivePageContentProps) => {
	const isLivePageExpToCoreExpMigrationEnabled = fg('live_page_exp_to_core_exp_migration');
	const livePageReleasePhase = getLivePageReleasePhase();

	useAbortNonLiveExperiences(isLivePageExpToCoreExpMigrationEnabled);

	const LivePageWrapper = ClassicEditorContextProviderLoader ?? React.Fragment;
	return (
		<LivePageWrapper>
			<StartLivePageLoad contentId={contentId} viewMode={!canEdit} />
			<ContentViewedEvent contentId={contentId} />
			<LivePagesChangeboardingManager origin="live-page" />
			{!isLivePageExpToCoreExpMigrationEnabled ? (
				<ExperienceStart name={LIVE_PAGE_VIEW_EXPERIENCE} />
			) : null}
			{isLivePageExpToCoreExpMigrationEnabled ? (
				<ExperienceSuccess
					name={CREATE_PAGE_EXPERIENCE}
					attributes={{ isLivePage: true, livePageReleasePhase }}
				/>
			) : null}
			<LivePageSessionExperienceStart />
			<NativeCollabLoader
				contentId={contentId}
				spaceKey={spaceKey}
				contentType="page"
				key={contentId}
				onPermissionChange={refetch}
			>
				{(propsFromNativeCollab: NativeCollabInjectedProps) => {
					const { viewMode } = propsFromNativeCollab;
					// TODO MODES-3871 Remove this triggerViewedAnalytics side effect when we cleanup LivePageComponent.
					// Trigger live page view analytics event once when the user views a live page.
					onNativeCollabLoad?.(contentId, viewMode);

					return (
						// This TreeRootForServerPlaceholderWrapper placed here to ensure the proper lookup of the placeholders for
						// SSRed loadables. Due to different component trees between server (LivePageComponent) and client (LivePageComponentServer)
						// the placeholder ids hierarchy is different. This wrapper terminates the lookup for placeholder ids on the common parent,
						// so it matches on both server and client.
						<TreeRootForServerPlaceholderWrapper>
							<FullPageEditorLoader
								{...propsFromNativeCollab}
								extensionContextProvider={{
									ClassicEditorContextProvider: ClassicEditorContextProviderLoader,
								}}
								hasEditPermission={canEdit}
								customPerfData={customPerfData}
							/>
							{fg('live_pages_mode_switcher') && <LivePageShortcuts canEdit={canEdit} />}
						</TreeRootForServerPlaceholderWrapper>
					);
				}}
			</NativeCollabLoader>
		</LivePageWrapper>
	);
};

export const LivePageComponent = ({
	contentId,
	spaceKey,
	ClassicEditorContextProviderLoader,
	customPerfData,
}: LivePageComponentProps) => {
	const { canEdit, loading, contentQueryError, refetch, networkStatus } =
		usePermissionCheck(contentId);
	const prevCanEditRef = useRef<boolean | undefined>(undefined);
	const hasSentViewedAnalytics = useRef<boolean>(false);
	const pageRef = useRef<{ isFirstLivePage?: boolean; contentId?: string }>({
		isFirstLivePage: false,
		contentId: undefined,
	});

	const { setMode } = useLivePageStoreActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isPreloading } = useEditorDataLoader({
		contentId,
		spaceKey,
	});
	const featureCodes = useFeatureCodesInURL();
	const {
		isEligibleForLivePageNudge,
		showSpotlight,
		markFirstLivePageAsSeen,
		livePageNudgeHasSeenFirstLivePage,
	} = useLivePageOnboardingNudgeEligible({
		contentId,
	});

	useEffect(() => {
		hasSentViewedAnalytics.current = false;

		return () => {
			// Reset the mode when contentId changes or component unmounts
			void setMode(undefined);
			prevCanEditRef.current = undefined;
		};
	}, [contentId, setMode]);

	useEffect(() => {
		// Permissions on a page can change at anytime. Should update the mode if the new permissions are different from the previous ones.
		const hasPermissionsChanged = canEdit !== prevCanEditRef.current;
		if (hasPermissionsChanged) {
			void setMode(canEdit ? 'edit' : 'view');
		}
		prevCanEditRef.current = canEdit;
	}, [canEdit, contentId, setMode]);

	useEffect(() => {
		if (isEligibleForLivePageNudge) {
			if (!livePageNudgeHasSeenFirstLivePage) {
				markFirstLivePageAsSeen();
				pageRef.current.isFirstLivePage = true;
				pageRef.current.contentId = contentId;
			}

			if (
				!loading &&
				// when live page has been seen before and it's not the current page
				livePageNudgeHasSeenFirstLivePage &&
				(!pageRef.current.isFirstLivePage || contentId !== pageRef.current.contentId)
			) {
				showSpotlight();
			}
		}

		// handles edge case where we visit another page where user didn't contribute
		if (contentId !== pageRef.current.contentId) {
			pageRef.current.isFirstLivePage = false;
		}
	}, [
		loading,
		isEligibleForLivePageNudge,
		markFirstLivePageAsSeen,
		showSpotlight,
		livePageNudgeHasSeenFirstLivePage,
		contentId,
	]);

	const handleNativeCollabLoad = (contentId: string, viewMode: boolean | undefined) => {
		if (hasSentViewedAnalytics.current === false) {
			void triggerViewedAnalytics({
				contentId,
				editPermission: canEdit,
				createAnalyticsEvent,
				viewMode,
				...(featureCodes && featureCodes.from ? { from: featureCodes.from } : {}),
			});
			hasSentViewedAnalytics.current = true;
		}
	};

	if (contentQueryError) {
		return <ErrorDisplay error={contentQueryError} />;
	}

	const skipLoadingScreenWhenSSRed = window.__SSR_RENDERED__;
	if (
		(isPreloading && !skipLoadingScreenWhenSSRed) ||
		// don't unmount live pages editor on permission refetch
		(loading && networkStatus !== NetworkStatus.refetch)
	) {
		return (
			<Box xcss={spinnerContainerStyles}>
				<Spinner interactionName="live-pages-loading-spinner" size="medium" />
			</Box>
		);
	}

	return (
		<LivePageContent
			contentId={contentId}
			spaceKey={spaceKey}
			ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
			canEdit={canEdit}
			refetch={refetch}
			onNativeCollabLoad={handleNativeCollabLoad}
			customPerfData={customPerfData}
		/>
	);
};
